/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { css, cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { token } from '@atlaskit/tokens';

import { useSearchTriggerInput } from '../search-trigger-context';
import { messages } from './messages';

// TODO: connect with ADS to figure out the best approach to use dynamic value with cssMap, to avoid manually use the csm token
const styles = cssMap({
	pressable: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		aspectRatio: '1',
		height: '36px',
		minHeight: '32px',
		borderRadius: token('radius.full', '50%'),
		backgroundColor: token('color.background.neutral'),
		border: 'none',
		'&:focus-visible': {
			backgroundColor: `var(--csm-color-400, ${token('color.background.neutral.pressed')})`,
			outlineColor: 'var(--csm-color-800)',
		},
	},
	usedState: {
		backgroundColor: `var(--csm-color-400, ${token('color.background.neutral')})`,
		'&:hover': {
			color: `var(--csm-color-icon, ${token('color.text')})`,
			backgroundColor: `var(--csm-color-700, ${token('color.background.neutral.hovered')})`,
		},
		'&:active': {
			color: token('color.text.inverse'),
			backgroundColor: `var(--csm-color-1000, ${token('color.background.neutral.pressed')})`,
			transition: token('motion.button.pressed'),
		},
		transition: token('motion.button.hovered'),
	},
	cursorPointer: {
		cursor: 'pointer',
	},
});

const buttonMotionStyles = css({
	transition: token('motion.button.hovered'),
	'&:active': {
		transition: token('motion.button.pressed'),
	},
});

type ButtonProps = { as: 'button' } & React.ButtonHTMLAttributes<HTMLButtonElement>;
type DivProps = { as: 'div' } & React.HTMLAttributes<HTMLDivElement>;

type ComponentProps = ButtonProps | DivProps;

export const SearchTriggerSubmitButton = ({ ...props }: ComponentProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { isExpanded, interactive } = useSearchTriggerInput();
	const isDimmed = !isExpanded;

	if (props.as === 'button') {
		const { as: _, ...rest } = props;
		return (
			<button
				aria-disabled={isDimmed}
				css={[
					styles.pressable,
					styles.cursorPointer,
					interactive && !isDimmed && styles.usedState,
					interactive &&
						!isDimmed &&
						fg('platform-dst-motion-uplift-custom-button') &&
						buttonMotionStyles,
				]}
				{...rest}
			>
				<ArrowRightIcon label={formatMessage(messages.searchButtonLabel)} />
			</button>
		);
	}

	const { as: _, ...rest } = props;
	return (
		<div css={[styles.pressable, interactive && !isDimmed && styles.usedState]} {...rest}>
			<ArrowRightIcon label={formatMessage(messages.searchButtonLabel)} />
		</div>
	);
};
